import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { AccountPlan } from '@/constants/enum';
import { camelToSnakeCase, checkDirtyField, isLockFeature } from '@/helpers';
import MenuItem from '@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/components/MenuItem';
import { convertClientKeyToApiKey } from '@/pages/TrackingPage/components/Language/config';
import UploadAsset from '@/pages/TrackingPage/components/LookAndFeel/components/ChooseTheme/UploadAsset';
import trackingPageSlice, { languageSelector, LanguageValue, trackingPageSelector } from '@/redux/features/trackingPage.slice';
import { Badge, BlockStack, Box, Button, Checkbox, Icon, InlineStack, Text, TextField } from '@shopify/polaris';
import { ChevronLeftIcon, EditIcon, InfoIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

enum LookupEditType {
  TrackingNumber = 'trackingNumber',
  OrderId = 'orderId',
  Email = 'emailId',
  Phone = 'phoneId',
  NotFoundMessage = 'notFoundMessage',
  TitleAndButton = 'titleAndButton',
  Image = 'image',
}

type LookupOptionKey =
  | 'isEnabledTrackingNumber'
  | 'isEnabledOrderId'
  | 'isEnabledEmail'
  | 'isEnabledPhoneNumber'
  | 'requireEmail'
  | 'requireOrderId'
  | 'requirePhoneNumber'
  | 'requireTrackingNumber'
  | 'isEnableSearchShipmentNoInfo'
  | 'isEnableSearchShipmentUnfulfill';

const Lookup = () => {
  const dispatch = useDispatch();
  const language = useSelector(languageSelector);
  const {
    value: data,
    fieldsChange,
    oldValue: {
      lookAndFeel: { lookupOption: oldLookupOption },
    },
  } = useSelector(trackingPageSelector);
  const { chooseTheme } = data.lookAndFeel;
  const {
    isEnabledEmail,
    isEnabledOrderId,
    isEnabledPhoneNumber,
    isEnabledTrackingNumber,
    requireEmail,
    requireOrderId,
    requirePhoneNumber,
    requireTrackingNumber,
    isEnableSearchShipmentNoInfo,
    isEnableSearchShipmentUnfulfill,
  } = data.lookAndFeel.lookupOption;
  const [currentEditItem, setCurrentEditItem] = useState<LookupEditType>();
  const lookupOptions: {
    id: LookupEditType;
    key: LookupOptionKey;
    isChecked: boolean | undefined;
    label: string;
  }[] = useMemo(() => {
    return [
      {
        id: LookupEditType.TrackingNumber,
        key: 'isEnabledTrackingNumber',
        isChecked: isEnabledTrackingNumber,
        label: 'Tracking number',
      },
      { id: LookupEditType.OrderId, key: 'isEnabledOrderId', isChecked: isEnabledOrderId, label: 'Order number' },
      {
        id: LookupEditType.Email,
        key: 'isEnabledEmail',
        isChecked: isEnabledEmail,
        label: 'Email',
      },
      {
        id: LookupEditType.Phone,
        key: 'isEnabledPhoneNumber',
        isChecked: isEnabledPhoneNumber,
        label: 'Phone number',
      },
    ];
  }, [isEnabledEmail, isEnabledOrderId, isEnabledPhoneNumber, isEnabledTrackingNumber]);

  const appearanceOptions = useMemo(() => {
    const res = [
      { id: LookupEditType.TitleAndButton, label: 'Title, description, button' },
      { id: LookupEditType.NotFoundMessage, label: 'Not found message' },
    ];
    if (chooseTheme === '2') {
      res.push({ id: LookupEditType.Image, label: 'Images' });
    }
    return res;
  }, [chooseTheme]);

  const handleLanguageField = useCallback(
    (currentValue: {}, parentKey: string) => {
      const { dirtyField: parentKeyField } = checkDirtyField(
        language.oldValue.value[parentKey as keyof typeof language.value],
        currentValue,
      );
      return !parentKeyField ? undefined : changeKeyOfObject(parentKeyField, parentKey);
    },
    [language],
  );

  const handleChangeLanguage = useCallback(
    (parentKey: string, key: string) => (value: string | boolean) => {
      const { fieldsChange } = language;
      dispatch(
        trackingPageSlice.actions.handleLanguageValue({
          ...language.value,
          [parentKey]: {
            ...language.value[parentKey as keyof LanguageValue],
            [key]: value,
          },
        }),
      );
      dispatch(trackingPageSlice.actions.handleChangeValidationLanguage(language.validation.filter((item) => item !== key)));
      dispatch(
        trackingPageSlice.actions.handleLanguageFieldsChange({
          ...fieldsChange,
          [parentKey]: handleLanguageField(
            {
              ...language.value[parentKey as keyof LanguageValue],
              [key]: value,
            },
            parentKey,
          ),
        }),
      );
    },
    [dispatch, handleLanguageField, language],
  );

  const changeKeyOfObject = (currentObject: Partial<Record<string, never>>, parentKey: string) => {
    const replacedObject = Object.keys(currentObject).map((key) => {
      const newKey =
        parentKey === 'trackingForm' || parentKey === 'footer'
          ? convertClientKeyToApiKey[parentKey][key as keyof (typeof convertClientKeyToApiKey)['trackingForm' | 'footer']]
          : camelToSnakeCase(key);
      return { [newKey]: currentObject[key] };
    });
    return replacedObject.reduce((a, b) => Object.assign({}, a, b));
  };

  const handleChangeLookupOption = useCallback(
    (key: LookupOptionKey) => (value: boolean) => {
      dispatch(
        trackingPageSlice.actions.handleTrackingPageValue({
          ...data,
          lookAndFeel: {
            ...data.lookAndFeel,
            lookupOption: {
              ...data.lookAndFeel.lookupOption,
              [key]: value,
            },
          },
        }),
      );
      const { dirtyField } = checkDirtyField(oldLookupOption, {
        ...data.lookAndFeel.lookupOption,
        [key]: value,
      });
      dispatch(
        trackingPageSlice.actions.handleTrackingPageFieldsChange({
          ...fieldsChange,
          isEnabledTrackingNumber: dirtyField?.isEnabledTrackingNumber,
          isEnabledOrderId: dirtyField?.isEnabledOrderId,
          isEnabledEmail: dirtyField?.isEnabledEmail,
          isEnabledPhoneNumber: dirtyField?.isEnabledPhoneNumber,
          isEnableSearchShipmentNoInfo: dirtyField?.isEnableSearchShipmentNoInfo,
          isEnableSearchShipmentUnfulfill: dirtyField?.isEnableSearchShipmentUnfulfill,
          trackingForm:
            dirtyField?.requireEmail !== undefined ||
            dirtyField?.requireOrderId !== undefined ||
            dirtyField?.requirePhoneNumber !== undefined ||
            dirtyField?.requireTrackingNumber !== undefined
              ? {
                  requireEmail: dirtyField?.requireEmail,
                  requireOrderId: dirtyField?.requireOrderId,
                  requirePhoneNumber: dirtyField?.requirePhoneNumber,
                  requireTrackingNumber: dirtyField?.requireTrackingNumber,
                }
              : undefined,
        }),
      );
    },
    [data, dispatch, fieldsChange, oldLookupOption],
  );

  const isLockedFeature = isLockFeature([AccountPlan.Starter]);

  const renderEditContent = useCallback(() => {
    switch (currentEditItem) {
      case LookupEditType.TrackingNumber:
        return (
          <>
            {/* <Checkbox
              label="This field is required"
              checked={requireTrackingNumber}
              onChange={handleChangeLookupOption('requireTrackingNumber')}
              disabled={isLockedFeature}
            /> */}
            <TextField
              maxLength={100}
              showCharacterCount
              label="Tracking number label"
              placeholder="Tracking number"
              autoComplete="off"
              value={language.value.trackingForm.trackingNumber}
              onChange={handleChangeLanguage('trackingForm', 'trackingNumber')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Tracking number placeholder"
              placeholder="Enter tracking number"
              autoComplete="off"
              value={language.value.trackingForm.placeholderTrackingNumber}
              onChange={handleChangeLanguage('trackingForm', 'placeholderTrackingNumber')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Empty tracking number error message"
              placeholder="Please enter your tracking number"
              autoComplete="off"
              value={language.value.trackingForm.emptyTrackingNumber}
              onChange={handleChangeLanguage('trackingForm', 'emptyTrackingNumber')}
              disabled={isLockedFeature}
            />
          </>
        );
      case LookupEditType.OrderId:
        return (
          <>
            {/* <Checkbox
              label="This field is required"
              checked={requireOrderId}
              onChange={handleChangeLookupOption('requireOrderId')}
              disabled={isLockedFeature}
            /> */}
            <TextField
              maxLength={100}
              showCharacterCount
              label="Order number label"
              placeholder="Order number"
              autoComplete="off"
              value={language.value.trackingForm.orderId}
              onChange={handleChangeLanguage('trackingForm', 'orderId')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Order number placeholder"
              placeholder="Enter your order number"
              autoComplete="off"
              value={language.value.trackingForm.placeholderOrderId}
              onChange={handleChangeLanguage('trackingForm', 'placeholderOrderId')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Empty Order number error message"
              placeholder="Please enter your order number"
              autoComplete="off"
              value={language.value.trackingForm.emptyOrderName}
              onChange={handleChangeLanguage('trackingForm', 'emptyOrderName')}
              disabled={isLockedFeature}
            />
          </>
        );
      case LookupEditType.Email:
        return (
          <>
            <Checkbox
              label="This field is required"
              checked={requireEmail}
              onChange={handleChangeLookupOption('requireEmail')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Email label"
              placeholder="Email"
              autoComplete="off"
              value={language.value.trackingForm.email}
              onChange={handleChangeLanguage('trackingForm', 'email')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Email placeholder"
              placeholder="Enter your email"
              autoComplete="off"
              value={language.value.trackingForm.placeholderEmail}
              onChange={handleChangeLanguage('trackingForm', 'placeholderEmail')}
              disabled={isLockedFeature}
            />
            {requireEmail ? (
              <TextField
                maxLength={100}
                showCharacterCount
                label="Empty email error message"
                placeholder="Please enter email"
                autoComplete="off"
                value={language.value.trackingForm.emptyEmail}
                onChange={handleChangeLanguage('trackingForm', 'emptyEmail')}
                disabled={isLockedFeature}
              />
            ) : null}
            <TextField
              maxLength={100}
              showCharacterCount
              label="Invalid email error message"
              placeholder="Invalid email address"
              autoComplete="off"
              value={language.value.trackingForm.invalidEmail}
              onChange={handleChangeLanguage('trackingForm', 'invalidEmail')}
              disabled={isLockedFeature}
            />
          </>
        );
      case LookupEditType.Phone:
        return (
          <>
            <Checkbox
              label="This field is required"
              checked={requirePhoneNumber}
              onChange={handleChangeLookupOption('requirePhoneNumber')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Phone number label"
              placeholder="Phone"
              autoComplete="off"
              value={language.value.trackingForm.phoneNumber}
              onChange={handleChangeLanguage('trackingForm', 'phoneNumber')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Phone number placeholder"
              placeholder="Enter your phone number"
              autoComplete="off"
              value={language.value.trackingForm.placeholderPhoneNumber}
              onChange={handleChangeLanguage('trackingForm', 'placeholderPhoneNumber')}
              disabled={isLockedFeature}
            />
            {requirePhoneNumber ? (
              <TextField
                maxLength={100}
                showCharacterCount
                label="Empty phone number error message"
                placeholder="Please enter phone number"
                autoComplete="off"
                value={language.value.trackingForm.emptyPhoneNumber}
                onChange={handleChangeLanguage('trackingForm', 'emptyPhoneNumber')}
                disabled={isLockedFeature}
              />
            ) : null}
            <TextField
              maxLength={100}
              showCharacterCount
              label="Invalid phone number error message"
              placeholder="Invalid phone number"
              autoComplete="off"
              value={language.value.trackingForm.invalidPhoneNumber}
              onChange={handleChangeLanguage('trackingForm', 'invalidPhoneNumber')}
              disabled={isLockedFeature}
            />
          </>
        );
      case LookupEditType.NotFoundMessage:
        return (
          <>
            <RegularText>Message when shipment has no information</RegularText>
            <TextField
              maxLength={200}
              showCharacterCount
              label="Order not found title"
              placeholder="Order number"
              autoComplete="off"
              multiline
              value={language.value.trackingForm.orderNotFound}
              onChange={handleChangeLanguage('trackingForm', 'orderNotFound')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={200}
              showCharacterCount
              label="Order not found message"
              placeholder="The search information may be incorrect or carrier has not updated tracking events. Please try later"
              autoComplete="off"
              multiline
              value={language.value.trackingForm.orderNotFoundMessage}
              onChange={handleChangeLanguage('trackingForm', 'orderNotFoundMessage')}
              disabled={isLockedFeature}
            />
          </>
        );
      case LookupEditType.TitleAndButton:
        return (
          <>
            <TextField
              maxLength={100}
              showCharacterCount
              label="Title"
              placeholder="Track your order"
              autoComplete="off"
              value={language.value.trackingForm.trackYourOrder}
              onChange={handleChangeLanguage('trackingForm', 'trackYourOrder')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={200}
              showCharacterCount
              label="Description"
              placeholder="Enter information below to check order status"
              autoComplete="off"
              value={language.value.trackingForm.orderDescription}
              onChange={handleChangeLanguage('trackingForm', 'orderDescription')}
              disabled={isLockedFeature}
            />
            <TextField
              maxLength={100}
              showCharacterCount
              label="Button"
              placeholder="Check status"
              autoComplete="off"
              value={language.value.trackingForm.track}
              onChange={handleChangeLanguage('trackingForm', 'track')}
              disabled={isLockedFeature}
            />
          </>
        );
      case LookupEditType.Image:
        return <UploadAsset isShowTrackingFormSetup />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentEditItem,
    requireTrackingNumber,
    handleChangeLookupOption,
    language.value.trackingForm.trackingNumber,
    language.value.trackingForm.placeholderTrackingNumber,
    language.value.trackingForm.emptyTrackingNumber,
    language.value.trackingForm.orderId,
    language.value.trackingForm.placeholderOrderId,
    language.value.trackingForm.emptyOrderName,
    language.value.trackingForm.email,
    language.value.trackingForm.placeholderEmail,
    language.value.trackingForm.emptyEmail,
    language.value.trackingForm.invalidEmail,
    language.value.trackingForm.phoneNumber,
    language.value.trackingForm.placeholderPhoneNumber,
    language.value.trackingForm.emptyPhoneNumber,
    language.value.trackingForm.orderNotFound,
    language.value.trackingForm.orderNotFoundMessage,
    language.value.trackingForm.trackYourOrder,
    language.value.trackingForm.orderDescription,
    language.value.trackingForm.track,
    handleChangeLanguage,
    requireOrderId,
    requireEmail,
    requirePhoneNumber,
    isLockedFeature,
  ]);

  return currentEditItem ? (
    <>
      <MenuItem>
        <div className="d-flex align-center gap-4">
          <Button
            icon={ChevronLeftIcon}
            onClick={() => setCurrentEditItem(undefined)}
            variant="monochromePlain"
            disabled={isLockedFeature}
          />
          <BoldText>{[...lookupOptions, ...appearanceOptions].find((item) => item.id === currentEditItem)?.label}</BoldText>
        </div>
      </MenuItem>
      <div className="pd-16 border-bottom d-flex flex-column gap-8 edit-content-left">{renderEditContent()}</div>
    </>
  ) : (
    <>
      <MenuItem isHeader>
        <BoldText>Order lookup page</BoldText>
      </MenuItem>

      <MenuItem tone="tertiary">
        <BoldText customFontSize>Lookup options</BoldText>
      </MenuItem>

      <Box padding={'400'} borderBlockEndWidth="0165" borderColor="border">
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd" fontWeight="medium">
            Allow to search
          </Text>
          <InlineStack align="space-between" blockAlign="center">
            <Checkbox
              label={
                <Text as="p" variant="bodyMd">
                  Fulfilled shipments with tracking information
                </Text>
              }
              checked={true}
              onChange={() => {}}
              disabled={true}
            />
            <Badge>Default</Badge>
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            Enable searching for other order types below. Customers can view basic details like order date and items.{' '}
            <Text as="span" fontWeight="bold">
              If disabled
            </Text>
            , these orders will result in{' '}
            <Text as="span" fontWeight="bold">
              Order not found
            </Text>
            .
          </Text>
          <Box paddingBlockStart={'200'}>
            <BlockStack gap="200">
              <InlineStack blockAlign="center">
                <Checkbox
                  label={
                    <Text as="p" variant="bodyMd">
                      Fulfilled shipments{' '}
                      <Text as="span" fontWeight="bold">
                        without
                      </Text>{' '}
                      tracking information
                    </Text>
                  }
                  checked={isEnableSearchShipmentNoInfo}
                  onChange={handleChangeLookupOption('isEnableSearchShipmentNoInfo')}
                  disabled={isLockedFeature}
                />
              </InlineStack>
              <InlineStack blockAlign="center">
                <Checkbox
                  label={
                    <Text as="p" variant="bodyMd">
                      Unfulfilled shipments
                    </Text>
                  }
                  checked={isEnableSearchShipmentUnfulfill}
                  onChange={handleChangeLookupOption('isEnableSearchShipmentUnfulfill')}
                  disabled={isLockedFeature}
                />
              </InlineStack>
            </BlockStack>
          </Box>
        </BlockStack>
      </Box>

      <Box padding={'400'} borderBlockEndWidth="0165" borderColor="border">
        <BlockStack gap="100">
          <Text as="p" variant="bodyMd">
            Method 1
          </Text>
          <InlineStack align="space-between">
            <Checkbox
              id={LookupEditType.TrackingNumber}
              label={
                <Text as="p" variant="bodyMd">
                  Tracking number
                </Text>
              }
              checked={isEnabledTrackingNumber}
              onChange={handleChangeLookupOption('isEnabledTrackingNumber')}
              disabled={isLockedFeature}
            />
            <Button
              icon={EditIcon}
              variant="plain"
              onClick={() => setCurrentEditItem(LookupEditType.TrackingNumber)}
              disabled={isLockedFeature}
            />
          </InlineStack>
          <InlineStack gap="100" blockAlign="center">
            <div style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center' }}>
              <Icon source={InfoIcon} tone="caution" />
            </div>
            <Text as="p" variant="bodySm" tone="caution">
              This is only available for orders with tracking information.
            </Text>
          </InlineStack>
        </BlockStack>
      </Box>
      <Box padding={'400'} borderBlockEndWidth="0165" borderColor="border">
        <BlockStack gap="100">
          <Text as="p" variant="bodyMd">
            Method 2
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            This method will appear as a separate tab
          </Text>
          <InlineStack align="space-between">
            <Checkbox
              id={LookupEditType.OrderId}
              label={
                <Text as="p" variant="bodyMd">
                  Order number
                </Text>
              }
              checked={isEnabledOrderId}
              onChange={handleChangeLookupOption('isEnabledOrderId')}
              disabled={isLockedFeature}
            />
            <Button
              icon={EditIcon}
              variant="plain"
              onClick={() => setCurrentEditItem(LookupEditType.OrderId)}
              disabled={isLockedFeature}
            />
          </InlineStack>
          {isEnabledOrderId && (
            <Box paddingInlineStart={'600'}>
              <InlineStack align="space-between">
                <Checkbox
                  id={LookupEditType.Email}
                  label={
                    <Text as="p" variant="bodyMd">
                      Email
                    </Text>
                  }
                  checked={isEnabledEmail}
                  onChange={handleChangeLookupOption('isEnabledEmail')}
                  disabled={isLockedFeature}
                />
                <Button
                  icon={EditIcon}
                  variant="plain"
                  onClick={() => setCurrentEditItem(LookupEditType.Email)}
                  disabled={isLockedFeature}
                />
              </InlineStack>
            </Box>
          )}
          {isEnabledOrderId && (
            <Box paddingInlineStart={'600'}>
              <InlineStack align="space-between">
                <Checkbox
                  id={LookupEditType.Phone}
                  label={
                    <Text as="p" variant="bodyMd">
                      Phone number
                    </Text>
                  }
                  checked={isEnabledPhoneNumber}
                  onChange={handleChangeLookupOption('isEnabledPhoneNumber')}
                  disabled={isLockedFeature}
                />
                <Button
                  icon={EditIcon}
                  variant="plain"
                  onClick={() => setCurrentEditItem(LookupEditType.Phone)}
                  disabled={isLockedFeature}
                />
              </InlineStack>
            </Box>
          )}
        </BlockStack>
      </Box>

      <MenuItem tone="tertiary">
        <BoldText customFontSize>Appearance</BoldText>
      </MenuItem>
      {appearanceOptions.map((item) => (
        <MenuItem actionIcon={EditIcon} onAction={() => setCurrentEditItem(item.id)} key={item.id} disabled={isLockedFeature}>
          <RegularText>{item.label}</RegularText>
        </MenuItem>
      ))}
    </>
  );
};

export default Lookup;
